import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './App.css';
import { FaArrowLeft } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';

function Team() {
  const navigate = useNavigate();
  
  // State to track mobile menu open/close
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // State to track if header is scrolled
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  
  // State to track scroll direction for header animation
  const [lastScrollY, setLastScrollY] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [showBackToTop, setShowBackToTop] = useState(false);
  
  // Close mobile menu when clicking a link
    const handleNavLinkClick = () => {
    setIsMenuOpen(false);
  };

  // Scroll smoothly to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Navigate to home page and scroll to contact section
  const handleRequestDemo = () => {
    handleNavLinkClick();
    // Navigate to home page with hash, which will trigger the scroll in App.js
    navigate('/#contact');
  };
  
  // Enhanced scroll behavior for header and back-to-top
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show back to top button when scrolled down past hero section
      setShowBackToTop(currentScrollY > 200);
      
      // Add scrolled class to header
      setIsHeaderScrolled(currentScrollY > 50);
      
      // Header visibility based on scroll direction
      // Only hide/show header if we're past the hero section
      if (currentScrollY > 150) {
        if (currentScrollY > lastScrollY) {
          // Scrolling down - hide header
          setHeaderVisible(false);
        } else {
          // Scrolling up - show header
          setHeaderVisible(true);
        }
      } else {
        // Always show header when near the top
        setHeaderVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };
    
    // Add passive option for better scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.3,
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  };

  // Team members
  const teamMembers = [
    {
      name: 'Shaik Hatim',
      title: 'Founder',
      img: '/hatim.png',
    },
    {
      name: 'Sahishnu Sagiraju',
      title: 'Founding Engineer',
      img: '/sahi.png',
    },
    {
      name: 'Adit Pathania',
      title: 'Founding Engineer',
      img: '/adit.png',
    },
    {
      name: 'Sherwin Thirumavalan',
      title: 'Full-Stack Developer',
      img: '/sherwin.png',
    },
    {
      name: 'Harkanwarveer Singh Kang',
      title: 'Mechanical Systems Engineer',
      img: '/hark.png',
    },
    {
      name: 'Aaryaman Shrestha',
      title: 'Mechanical Systems Engineer',
      img: '/pablo.png',
    },
    {
      name: 'Deric Thomas',
      title: 'Systems Analyst',
      img: '/deric.png',
    },
  ];

  return (
    <div className="app-container">
      {/* Header */}
      <header className={`header ${isHeaderScrolled ? 'scrolled' : ''} ${headerVisible ? 'visible' : 'hidden'}`}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <motion.div
            className="logo"
            whileHover={{ scale: 1.1 }}
            transition={{ type: 'spring', stiffness: 200 }}
          >
            SafeLine Pickup
          </motion.div>
        </Link>

        {/* Navigation links */}
        <nav className={isMenuOpen ? 'nav-mobile-open' : ''}>
          <ul className="nav-links">
            <li>
              <Link to="/#origin" onClick={handleNavLinkClick}>Product</Link>
            </li>
            <li>
              <Link to="/team" onClick={handleNavLinkClick}>Team</Link>
            </li>
            <li>
              <a href="#" onClick={(e) => { e.preventDefault(); handleRequestDemo(); }}>
                Request a Demo
              </a>
            </li>
          </ul>
          
          {/* Login Button - appears in mobile menu only */}
          <motion.div
            className="header-login-container mobile-only"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Link to="/login" className="login-button-header" onClick={handleNavLinkClick}>
              <span>Login</span>
            </Link>
          </motion.div>
        </nav>

        {/* Hamburger icon for mobile */}
        <div
          className={`hamburger ${isMenuOpen ? 'open' : ''}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <span />
          <span />
          <span />
        </div>

        {/* Login Button - desktop only */}
        <motion.div
          className="header-login-container desktop-only"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Link to="/login" className="login-button-header">
            <span>Login</span>
          </Link>
        </motion.div>
      </header>

      {/* Team Section */}
      <section className="ack-section" style={{ paddingTop: '120px' }}>
        <motion.h2
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          Our Team
        </motion.h2>
        
        <motion.div 
          className="ack-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {teamMembers.map((person, idx) => (
            <motion.div
              key={idx}
              className="ack-card"
              variants={itemVariants}
              whileHover={{ y: -10 }}
            >
              <div className="ack-img-wrapper">
                <img src={person.img} alt={person.name} className="ack-img" />
              </div>
              <h4>{person.name}</h4>
              <p>{person.title}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          style={{ marginTop: '3rem' }}
        >
          <Link to="/" className="back-link">
            <FaArrowLeft style={{ marginRight: '8px' }} /> Back to Product
          </Link>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-logo">SafeLine Pickup</div>
        <p>© {new Date().getFullYear()} SafeLine Pickup. All rights reserved.</p>
      </footer>

      {/* "Back to Top" button */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            className="back-to-top"
            onClick={scrollToTop}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            whileHover={{ y: -5 }}
          >
            ↑
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Team;
